'use client'

import { useState, useEffect, useRef, useCallback, useMemo, startTransition } from 'react'
import { useSession } from 'next-auth/react'
import { useToast } from "@/hooks/use-toast"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useAnalytics } from '@/components/providers/analytics-provider';
import { AddContactForm } from '@/components/add-contact-form';
import { AddUserForm } from '@/components/add-user-form';
import { cn } from '@/lib/utils';
import type { CommunityInviteSelectedAccount } from '@/components/community-invite-filter-panel';
import { parseOutlookStyleEmailInvites } from '@/lib/parse-outlook-style-email-invites';
import { ShareRoomEmailPreviewPanel } from '@/components/share-room/share-room-email-preview-panel';
import { ShareRoomSuccessOverlay } from '@/components/share-room/share-room-success-overlay';
import { ShareRoomCustomerEmailInviteForm } from '@/components/share-room/share-room-customer-email-invite-form';
import { ShareRoomDomainJoinSection } from '@/components/share-room/share-room-domain-join-section';
import { ShareRoomCommunityStepNav } from '@/components/share-room/share-room-community-step-nav';
import { ShareRoomInternalInviteColumn } from '@/components/share-room/share-room-internal-invite-column';
import { useShareRoomEmailPreview } from '@/components/share-room/use-share-room-email-preview';
import { useShareRoomInviteSubmit } from '@/hooks/use-share-room-invite-submit';
import type {
  ContactOrUser,
  InviteFetchOptions,
  ShareRoomEventData,
  ShareRoomModalProps,
  ShareRoomGuestInviteRow,
} from '@/components/share-room/types';
import { buildSharedRoomUrl } from '@/lib/shared-room/build-shared-room-url';
import { parseShareRoomDirectoryResponse } from '@/lib/share-room/directory';
import {
  filterInvitePickerItems,
  mergeUniqueShareRecipients,
  normalizeRecipientEmail,
  remainingAccountContacts,
} from '@/lib/share-room/invite-picker';

export type { ContactOrUser, ShareRoomModalProps, ShareRoomGuestInviteRow, ShareContentRoomMemberForSubmit } from '@/components/share-room/types';

export function ShareRoomModal({
  isOpen,
  onClose,
  roomId,
  roomName,
  accountId,
  accountName,
  contentName,
  contentId,
  onMembersUpdated,
  roomType: propRoomType,
  domainJoinEnabled = false,
  allowedJoinDomains = [],
}: ShareRoomModalProps) {
  const [contactsAndUsers, setContactsAndUsers] = useState<ContactOrUser[]>([]);
  const [directoryTruncated, setDirectoryTruncated] = useState(false);
  const [directoryLimit, setDirectoryLimit] = useState(0);
  const [selectedContactsAndUsers, setSelectedContactsAndUsers] = useState<ContactOrUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState('');
  const [createShareableLink, setCreateShareableLink] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const { toast } = useToast()
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [isShareModalOpen, setIsShareModalOpen] = useState(isOpen);
  const [generatedShareToken, setGeneratedShareToken] = useState<string | null>(null);
  /** When company-email join is on, we load `/shared/{token}` once for copy + invites. */
  const [domainShareLinkLoading, setDomainShareLinkLoading] = useState(false);
  /** Same room + modal reopen: keep URL visible; server still reuses token per POST. */
  const prevRoomIdForShareLinkCacheRef = useRef<string | null>(null);
  const domainShareLinkFetchedRoomIdRef = useRef<string | null>(null);
  const { trackEvent, roomId: contextRoomId, userType } = useAnalytics();
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [addContactInitialEmail, setAddContactInitialEmail] = useState('');
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [isSendingInvites, setIsSendingInvites] = useState(false);
  const [isSelectingAllRoomMembers, setIsSelectingAllRoomMembers] = useState(false);
  const [isInternalInRoomTenant, setIsInternalInRoomTenant] = useState(false);
  const [roomTenantId, setRoomTenantId] = useState<string | null>(null);
  const [isCommunityRoom, setIsCommunityRoom] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const { data: session } = useSession();
  const [communityWizardStep, setCommunityWizardStep] = useState(1);
  const [audienceMethod, setAudienceMethod] = useState<'filter' | 'quick'>('filter');
  const [filterAccountTypes, setFilterAccountTypes] = useState<string[]>([]);
  const [filterSelectedAccounts, setFilterSelectedAccounts] = useState<CommunityInviteSelectedAccount[]>([]);
  const [filterTitleContains, setFilterTitleContains] = useState('');
  const [filterIncludeNoAccount, setFilterIncludeNoAccount] = useState(false);
  const [filterIncludeInternalUsers, setFilterIncludeInternalUsers] = useState(false);
  /** Loading state for community filter apply only — avoids swapping the whole modal body. */
  const [communityFilterLoading, setCommunityFilterLoading] = useState(false);
  /** True after a successful filter-mode fetch so we show matches (not prefetched users) and the results panel without requiring focus on the search field. */
  const [communityFilterResultsReady, setCommunityFilterResultsReady] = useState(false);
  const communityFilterAutoAppliedRef = useRef(false);

  /** Multi-step “community audience” flow is for internal users only; external customers invite colleagues via the classic single-screen flow. */
  const isCommunityRoomContext =
    propRoomType?.toUpperCase() === 'COMMUNITY' ||
    (propRoomType === undefined && isCommunityRoom);
  const showCommunityInviteWizard =
    isCommunityRoomContext && session?.user?.userType === 'INTERNAL';

  /** Customers always use the simple email-invite experience for every room type. */
  const showCustomerEmailInviteUi = session?.user?.userType === 'EXTERNAL';

  /**
   * Partner-aware invite-by-email account context.
   * Only enabled when the room has at least one PARTNER account linked.
   */
  const [roomHasPartnerAccount, setRoomHasPartnerAccount] = useState(false)
  const [myCompanyAccount, setMyCompanyAccount] = useState<{
    id: string
    name: string
  } | null>(null)
  const [emailInviteAccountContext, setEmailInviteAccountContext] = useState<
    'my_company' | 'room_customer' | 'no_account'
  >('room_customer')

  const [guestInviteRows, setGuestInviteRows] = useState<ShareRoomGuestInviteRow[]>([]);
  const [guestEmailField, setGuestEmailField] = useState('');
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [bulkInvitePasteOpen, setBulkInvitePasteOpen] = useState(false);
  const [bulkInvitePasteText, setBulkInvitePasteText] = useState('');
  /** COMMUNITY + internal: CRM context for pasted / queued email invites */
  const [communityBulkNoAccount, setCommunityBulkNoAccount] = useState(false);
  const [communityBulkAccountId, setCommunityBulkAccountId] = useState<string | null>(null);
  const [communityBulkAccountName, setCommunityBulkAccountName] = useState<string | null>(null);
  const [communityBulkAccountSearch, setCommunityBulkAccountSearch] = useState('');
  const [communityBulkAccountHits, setCommunityBulkAccountHits] = useState<
    { id: string; name: string }[]
  >([]);
  const [communityBulkAccountSearchLoading, setCommunityBulkAccountSearchLoading] = useState(false);
  const generatedLink = useMemo(() => {
    if (!generatedShareToken) return null;

    const origin =
      typeof window !== 'undefined'
        ? window.location.origin
        : (process.env.NEXT_PUBLIC_APP_URL || '').replace(/\/$/, '');

    return origin
      ? buildSharedRoomUrl(origin, generatedShareToken, contentId)
      : null;
  }, [generatedShareToken, contentId]);

  const isPartnerExternalUser =
    showCustomerEmailInviteUi &&
    roomHasPartnerAccount &&
    Boolean(myCompanyAccount?.id) &&
    myCompanyAccount?.id !== accountId

  const handleCloseConfirmation = useCallback(() => {
    setShowConfirmation(false);
    setShowConfetti(false);
    onClose();
  }, [onClose]);

  const handleConfettiFade = useCallback(() => {
    setShowConfetti(false);
  }, []);

  const resolveExternalInviteContext = useCallback(() => {
    if (!isPartnerExternalUser) {
      return {
        inviteAccountId: accountId || null,
        inviteContactsWithoutAccount: false,
      }
    }
    if (emailInviteAccountContext === 'no_account') {
      return {
        inviteAccountId: null,
        inviteContactsWithoutAccount: true,
      }
    }
    if (emailInviteAccountContext === 'room_customer') {
      return {
        inviteAccountId: accountId || null,
        inviteContactsWithoutAccount: false,
      }
    }
    return {
      inviteAccountId: myCompanyAccount?.id ?? (accountId || null),
      inviteContactsWithoutAccount: false,
    }
  }, [
    isPartnerExternalUser,
    emailInviteAccountContext,
    myCompanyAccount?.id,
    accountId,
  ])

  const handleTrackEvent = useCallback((eventData: ShareRoomEventData) => {
    if (contextRoomId) {  // Only track if in room context
      trackEvent({
        ...eventData,
        roomId: contextRoomId,
        userType
      });
    }
  }, [contextRoomId, trackEvent, userType]);

  const handleCopyShareLink = useCallback(() => {
    if (!generatedLink) return
    void navigator.clipboard.writeText(generatedLink)
    toast({
      title: 'Copied',
      description: 'Share link copied to clipboard.',
    })
  }, [generatedLink, toast])

  const handleCreateShareableLinkToggle = useCallback(
    async (checked: boolean) => {
      setCreateShareableLink(checked)
      if (checked && !generatedShareToken) {
        try {
          const response = await fetch('/api/share-link', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              roomId,
              expiresAt: null,
            }),
          })

          const data = await response.json()

          if (response.ok && data.shareLink) {
            setGeneratedShareToken(data.shareLink.token)

            handleTrackEvent({
              eventType: 'share_room_link',
              metadata: {
                shareToken: data.shareLink.token,
                roomName,
                accountName,
              },
            })
          }
        } catch (error) {
          console.error('Error generating share link:', error)
          toast({
            title: 'Error creating link',
            description: 'There was a problem creating the shareable link.',
            variant: 'destructive',
          })
          setCreateShareableLink(false)
        }
      }
    },
    [generatedShareToken, roomId, roomName, accountName, toast, handleTrackEvent]
  )

  const isCommunityFilterStep2 =
    showCommunityInviteWizard &&
    communityWizardStep === 2 &&
    audienceMethod === 'filter';

  const invitePickerItems = useMemo(
    () =>
      filterInvitePickerItems({
        pool: contactsAndUsers,
        selected: selectedContactsAndUsers,
        searchTerm,
        isCommunityFilterPending: isCommunityFilterStep2 && !communityFilterResultsReady,
      }),
    [
      contactsAndUsers,
      selectedContactsAndUsers,
      searchTerm,
      isCommunityFilterStep2,
      communityFilterResultsReady,
    ]
  );

  const addAllContacts = useMemo(
    () => remainingAccountContacts(contactsAndUsers, selectedContactsAndUsers),
    [contactsAndUsers, selectedContactsAndUsers]
  );

  // Fetch user context and room type for the room
  const fetchUserContext = useCallback(async () => {
    if (!roomId) return;
    try {
      const response = await fetch(`/api/rooms/${roomId}/user-context`);
      if (response.ok) {
        const data = await response.json();
        setIsInternalInRoomTenant(data.isInternal || false);
        setRoomTenantId(data.roomTenantId || null);
      }
      
      // Fetch room details to check room type (only if not provided as prop)
      if (!propRoomType) {
        const roomResponse = await fetch(`/api/rooms/${roomId}`);
        if (roomResponse.ok) {
          const roomData = await roomResponse.json();
          // API returns lowercase, normalize to uppercase for comparison
          const roomTypeUpper = (roomData.roomType || '').toUpperCase();
          const isCommunity = roomTypeUpper === 'COMMUNITY';
          setIsCommunityRoom(isCommunity);
        }
      } else {
        // Use room type from prop (synchronous, no async delay)
        // Normalize to uppercase for comparison
        const roomTypeUpper = (propRoomType || '').toUpperCase();
        const isCommunity = roomTypeUpper === 'COMMUNITY';
        setIsCommunityRoom(isCommunity);
      }
    } catch (error) {
      console.error('Error fetching user context:', error);
      // Fallback to session userType if API fails
      setIsInternalInRoomTenant(session?.user?.userType === 'INTERNAL');
      setRoomTenantId(session?.user?.tenantId || null);
    }
  }, [roomId, session?.user?.userType, session?.user?.tenantId, propRoomType]);

  // For invite-by-email (external users), detect partner participation + derive default account context.
  useEffect(() => {
    if (!isOpen || !roomId) return
    if (!showCustomerEmailInviteUi) return

    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`/api/rooms/${roomId}/accounts`)
        if (!res.ok) return
        const data = await res.json()
        const rows = (data?.roomAccounts ?? []) as Array<{ relationshipType?: string }>
        const hasPartner = rows.some((r) => r.relationshipType === 'PARTNER')
        if (cancelled) return

        setRoomHasPartnerAccount(hasPartner)
        if (!hasPartner) {
          setMyCompanyAccount(null)
          setEmailInviteAccountContext('room_customer')
          return
        }

        const myRes = await fetch(`/api/rooms/${roomId}/my-company`)
        if (!myRes.ok) return
        const myData = await myRes.json()
        const myId = typeof myData?.accountId === 'string' ? myData.accountId : null
        const myName = typeof myData?.accountName === 'string' ? myData.accountName : null

        if (cancelled) return
        if (myId && myName) {
          setMyCompanyAccount({ id: myId, name: myName })
          setEmailInviteAccountContext(myId !== accountId ? 'my_company' : 'room_customer')
        } else {
          setMyCompanyAccount(null)
          setEmailInviteAccountContext('room_customer')
        }
      } catch {
        // Silent; defaults remain.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isOpen, roomId, showCustomerEmailInviteUi, accountId])

  // Set isCommunityRoom immediately from prop if available (synchronous, no async delay)
  useEffect(() => {
    if (propRoomType) {
      const roomTypeUpper = (propRoomType || '').toUpperCase();
      const isCommunity = roomTypeUpper === 'COMMUNITY';
      setIsCommunityRoom(isCommunity);
    }
  }, [propRoomType]);

  const fetchContactsAndUsers = useCallback(
    async (searchTerm?: string, options?: InviteFetchOptions) => {
      if (!roomId) {
        return;
      }

      if (session?.user?.userType === 'EXTERNAL' && !options?.filterMode) {
        startTransition(() => {
          setContactsAndUsers([]);
          setDirectoryTruncated(false);
          setDirectoryLimit(0);
        });
        return;
      }

      const wasFocused = document.activeElement === inputRef.current;
      const applyDirectory = (
        data: unknown,
        mapItems?: (items: ContactOrUser[]) => ContactOrUser[]
      ) => {
        const parsed = parseShareRoomDirectoryResponse(data);
        const items = parsed.items as ContactOrUser[];
        startTransition(() => {
          setContactsAndUsers(mapItems ? mapItems(items) : items);
          setDirectoryTruncated(parsed.truncated);
          setDirectoryLimit(parsed.limit);
        });
      };

      if (isCommunityRoom && !searchTerm && !options?.filterMode) {
        try {
          const queryParams = new URLSearchParams({
            roomId,
            ...(contentName ? { includeExisting: 'true' } : {}),
          });
          const response = await fetch(`/api/share-room?${queryParams.toString()}`);
          if (!response.ok) throw new Error('Failed to fetch users');
          const data = await response.json();
          applyDirectory(data, (items) => items.filter((item) => item.type === 'user'));
        } catch (error) {
          console.error('Error fetching users:', error);
          toast({
            title: 'Error fetching users',
            description: 'An unexpected error occurred. Please try again.',
            variant: 'destructive',
          });
        }
        if (wasFocused && inputRef.current) {
          setTimeout(() => inputRef.current?.focus(), 0);
        }
        return;
      }

      const isFilterRequest = Boolean(options?.filterMode);
      if (!searchTerm && !isFilterRequest) {
        setIsLoading(true);
      }

      try {
        const queryParams = new URLSearchParams({
          roomId,
          ...(searchTerm ? { search: searchTerm } : {}),
          ...(!isCommunityRoom && accountId ? { accountId } : {}),
          ...(contentName ? { includeExisting: 'true' } : {}),
                ...(isFilterRequest
            ? {
                filterMode: 'true',
                ...(options?.accountTypes?.length
                  ? { accountTypes: options.accountTypes.join(',') }
                  : {}),
                ...(options?.filterAccountIds?.length
                  ? { accountIds: options.filterAccountIds.join(',') }
                  : {}),
                ...(options?.titleContains ? { titleContains: options.titleContains } : {}),
                ...(options?.includeContactsWithoutAccount
                  ? { includeContactsWithoutAccount: 'true' }
                  : {}),
                includeInternalUsers:
                  options?.includeInternalUsers === false ? 'false' : 'true',
              }
            : {}),
        });
        const response = await fetch(`/api/share-room?${queryParams.toString()}`);
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('[ShareRoomModal] API error:', response.status, errorData);
          throw new Error(errorData.error || 'Failed to fetch contacts and users');
        }
        const data = await response.json();
        applyDirectory(data);
        if (isFilterRequest) {
          setCommunityFilterResultsReady(true);
          setShowSuggestions(true);
        }

        if (wasFocused && inputRef.current) {
          requestAnimationFrame(() => {
            if (inputRef.current && document.activeElement !== inputRef.current) {
              inputRef.current.focus();
              const length = inputRef.current.value.length;
              inputRef.current.setSelectionRange(length, length);
            }
          });
        }
      } catch (error) {
        console.error('Error fetching contacts and users:', error);
        toast({
          title: 'Error fetching contacts and users',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        });
      } finally {
        if (!searchTerm && !isFilterRequest) {
          setIsLoading(false);
        }
        if (document.activeElement !== inputRef.current && inputRef.current) {
          setTimeout(() => inputRef.current?.focus(), 0);
        }
      }
    },
    [accountId, roomId, toast, contentName, isCommunityRoom, session?.user?.userType]
  );

  const toggleFilterAccountType = useCallback((value: string, checked: boolean) => {
    setFilterAccountTypes((prev) => {
      if (checked) return prev.includes(value) ? prev : [...prev, value];
      return prev.filter((v) => v !== value);
    });
  }, []);

  const addCommunityFilterAccount = useCallback((account: CommunityInviteSelectedAccount) => {
    setFilterSelectedAccounts((prev) =>
      prev.some((a) => a.id === account.id) ? prev : [...prev, account]
    );
  }, []);

  const removeCommunityFilterAccount = useCallback((accountId: string) => {
    setFilterSelectedAccounts((prev) => prev.filter((a) => a.id !== accountId));
  }, []);

  const communityInviteFilterOptions = useMemo<InviteFetchOptions>(
    () => ({
      filterMode: true,
      accountTypes: filterAccountTypes,
      filterAccountIds: filterSelectedAccounts.map((a) => a.id),
      titleContains: filterTitleContains.trim(),
      includeContactsWithoutAccount: filterIncludeNoAccount,
      includeInternalUsers: filterIncludeInternalUsers,
    }),
    [
      filterAccountTypes,
      filterSelectedAccounts,
      filterTitleContains,
      filterIncludeNoAccount,
      filterIncludeInternalUsers,
    ]
  );

  const applyCommunityFilters = useCallback(async () => {
    const opts = communityInviteFilterOptions;
    const hasFilter =
      (opts.accountTypes?.length ?? 0) > 0 ||
      (opts.filterAccountIds?.length ?? 0) > 0 ||
      (opts.titleContains?.length ?? 0) > 0 ||
      Boolean(opts.includeContactsWithoutAccount) ||
      Boolean(opts.includeInternalUsers);
    if (!hasFilter) {
      toast({
        title: 'Choose at least one filter',
        description:
          'Pick specific accounts and/or account types, enter a title keyword, include contacts with no account, or include internal teammates.',
        variant: 'destructive',
      });
      return;
    }
    setCommunityFilterLoading(true);
    try {
      await fetchContactsAndUsers(undefined, opts);
    } finally {
      setCommunityFilterLoading(false);
    }
  }, [fetchContactsAndUsers, communityInviteFilterOptions, toast]);

  useEffect(() => {
    if (!isOpen || !showCommunityInviteWizard || communityWizardStep !== 2 || audienceMethod !== 'filter') {
      return;
    }
    if (communityFilterAutoAppliedRef.current) return;
    const opts = communityInviteFilterOptions;
    const hasFilter =
      (opts.accountTypes?.length ?? 0) > 0 ||
      (opts.filterAccountIds?.length ?? 0) > 0 ||
      (opts.titleContains?.length ?? 0) > 0 ||
      Boolean(opts.includeContactsWithoutAccount) ||
      Boolean(opts.includeInternalUsers);
    if (!hasFilter) return;
    communityFilterAutoAppliedRef.current = true;
    void applyCommunityFilters();
  }, [
    isOpen,
    showCommunityInviteWizard,
    communityWizardStep,
    audienceMethod,
    communityInviteFilterOptions,
    applyCommunityFilters,
  ]);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(false);
      setSelectedContactsAndUsers([]);
      setDirectoryTruncated(false);
      setDirectoryLimit(0);
      setSearchTerm('');
      setMessage('');
      setCreateShareableLink(false);
      setShowPreview(false);
      setCommunityWizardStep(1);
      setAudienceMethod('filter');
      setFilterAccountTypes([]);
      setFilterSelectedAccounts([]);
      setFilterTitleContains('');
      setFilterIncludeNoAccount(false);
      setFilterIncludeInternalUsers(false);
      communityFilterAutoAppliedRef.current = false;
      setCommunityFilterResultsReady(false);
      setCommunityFilterLoading(false);
      setGuestInviteRows([]);
      setGuestEmailField('');
      setGuestFirstName('');
      setGuestLastName('');
      setBulkInvitePasteOpen(false);
      setBulkInvitePasteText('');
      setCommunityBulkNoAccount(false);
      setCommunityBulkAccountId(null);
      setCommunityBulkAccountName(null);
      setCommunityBulkAccountSearch('');
      setCommunityBulkAccountHits([]);
      // Avoid waiting on user-context round-trip before directory fetch / Add Contact tenant id
      setRoomTenantId(session?.user?.tenantId ?? null);
      fetchUserContext(); // Refines tenant + internal flags for this room
    }
  }, [isOpen, fetchUserContext, session?.user?.tenantId]);

  useEffect(() => {
    const prev = prevRoomIdForShareLinkCacheRef.current;
    if (prev === null) {
      prevRoomIdForShareLinkCacheRef.current = roomId;
      return;
    }
    if (prev !== roomId) {
      prevRoomIdForShareLinkCacheRef.current = roomId;
      setGeneratedShareToken(null);
      domainShareLinkFetchedRoomIdRef.current = null;
    }
  }, [roomId]);

  useEffect(() => {
    if (!isOpen || contentName || !domainJoinEnabled || !roomId) return;
    if (domainShareLinkFetchedRoomIdRef.current === roomId && generatedShareToken) {
      return;
    }

    let cancelled = false;
    setDomainShareLinkLoading(true);

    void (async () => {
      try {
        const response = await fetch('/api/share-link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ roomId, expiresAt: null }),
        });
        const data = (await response.json()) as { shareLink?: { token: string } };
        if (cancelled) return;
        if (response.ok && data.shareLink) {
          setGeneratedShareToken(data.shareLink.token);
          setCreateShareableLink(true);
          domainShareLinkFetchedRoomIdRef.current = roomId;
          handleTrackEvent({
            eventType: 'share_room_link',
            metadata: {
              shareToken: data.shareLink.token,
              roomName,
              accountName,
            },
          });
        } else {
          toast({
            title: 'Could not load share link',
            description: 'Try again or contact support if this persists.',
            variant: 'destructive',
          });
        }
      } catch (e) {
        console.error('Error loading share link for domain join:', e);
        if (!cancelled) {
          toast({
            title: 'Could not load share link',
            description: 'An unexpected error occurred.',
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setDomainShareLinkLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    contentName,
    domainJoinEnabled,
    roomId,
    roomName,
    accountName,
    generatedShareToken,
    handleTrackEvent,
    toast,
  ]);

  useEffect(() => {
    if (showCommunityInviteWizard && communityWizardStep === 1) {
      communityFilterAutoAppliedRef.current = false;
    }
  }, [showCommunityInviteWizard, communityWizardStep]);

  useEffect(() => {
    if (isOpen && showCommunityInviteWizard && communityWizardStep === 2) {
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [isOpen, showCommunityInviteWizard, communityWizardStep]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Fetch directory as soon as the modal opens — share-room API authorizes; don’t wait on user-context.
  useEffect(() => {
    if (!isOpen || !roomId) return;
    if (session?.user?.userType === 'EXTERNAL') return;
    fetchContactsAndUsers();
  }, [isOpen, roomId, isCommunityRoom, fetchContactsAndUsers, session?.user?.userType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideInput = inputRef.current?.contains(target);
      const isInsideSuggestions = suggestionsRef.current?.contains(target);

      if (!isInsideInput && !isInsideSuggestions) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectContactOrUser = useCallback((item: ContactOrUser) => {
    setSelectedContactsAndUsers((prev) => {
      if (prev.some((c) => c.id === item.id)) return prev;
      const email = normalizeRecipientEmail(item.email);
      if (email && prev.some((c) => normalizeRecipientEmail(c.email) === email)) {
        return prev;
      }
      return [...prev, item];
    });
    setSearchTerm('');
    setShowSuggestions(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  }, []);

  const handleRemoveContactOrUser = useCallback((id: string) => {
    setSelectedContactsAndUsers(prev => prev.filter(c => c.id !== id));
  }, []);

  const handleSelectAllRoomMembers = useCallback(async () => {
    if (!roomId || !contentName) return;

    setIsSelectingAllRoomMembers(true);
    try {
      const params = new URLSearchParams({ forContentShare: '1', limit: '50' });
      const response = await fetch(`/api/rooms/${roomId}/members?${params.toString()}`);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || 'Could not load room members');
      }

      const data = (await response.json()) as Array<{
        id: string;
        name: string;
        email: string;
        role?: string;
        avatar?: string;
      }>;

      const currentUserId = session?.user?.id;
      const members: ContactOrUser[] = (Array.isArray(data) ? data : [])
        .filter((member) => member.id && member.id !== currentUserId)
        .map((member) => {
          const fullName = (member.name || '').trim();
          const spaceIndex = fullName.indexOf(' ');
          const firstName = spaceIndex === -1 ? fullName : fullName.slice(0, spaceIndex).trim();
          const lastName = spaceIndex === -1 ? '' : fullName.slice(spaceIndex + 1).trim();

          return {
            id: member.id,
            firstName: firstName || fullName || 'Member',
            lastName,
            email: (member.email || '').trim(),
            title: member.role || '',
            type: 'user' as const,
            image: member.avatar,
          };
        });

      setSelectedContactsAndUsers((prev) => {
        const idSet = new Set(prev.map((p) => p.id));
        const emailSet = new Set(
          prev
            .map((p) => normalizeRecipientEmail(p.email))
            .filter((email): email is string => Boolean(email))
        );
        const additions = members.filter((m) => {
          if (idSet.has(m.id)) return false;
          const email = normalizeRecipientEmail(m.email);
          return !(email && emailSet.has(email));
        });
        return additions.length > 0 ? [...prev, ...additions] : prev;
      });
      setSearchTerm('');
      setShowSuggestions(false);
    } catch (error) {
      console.error('Error selecting all room members:', error);
      toast({
        title: 'Could not select room members',
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsSelectingAllRoomMembers(false);
    }
  }, [roomId, contentName, session?.user?.id, toast]);

  const handleAddAllContacts = useCallback(() => {
    if (addAllContacts.length === 0) return;
    setSelectedContactsAndUsers((prev) => mergeUniqueShareRecipients(prev, addAllContacts));
    setSearchTerm('');
    setShowSuggestions(false);
    if (directoryTruncated) {
      toast({
        title: 'Added loaded contacts',
        description:
          'More contacts exist on this account than we loaded. Type a name or email to find someone else.',
      });
    }
  }, [addAllContacts, directoryTruncated, toast]);

  const selectedIdSet = useMemo(
    () => new Set(selectedContactsAndUsers.map((s) => s.id)),
    [selectedContactsAndUsers]
  );

  const isCommunityWizardStep2 = showCommunityInviteWizard && communityWizardStep === 2;

  /** Rows for the community step-2 table: current API results plus any selections so users can always uncheck. */
  const communityInviteTableRows = useMemo(() => {
    if (!isCommunityWizardStep2) return [];

    const trimmed = searchTerm.trim();
    const matchesSearch = (item: ContactOrUser) => {
      if (!trimmed) return true;
      const q = trimmed.toLowerCase();
      return (
        item.firstName.toLowerCase().includes(q) ||
        item.lastName.toLowerCase().includes(q) ||
        item.email.toLowerCase().includes(q)
      );
    };

    let fromApi: ContactOrUser[] = [];
    if (isCommunityFilterStep2 && !communityFilterResultsReady) {
      fromApi = [];
    } else if (isCommunityFilterStep2) {
      fromApi = contactsAndUsers.filter(matchesSearch);
    } else if (audienceMethod === 'quick') {
      fromApi = contactsAndUsers.filter(matchesSearch);
    }

    const byId = new Map<string, ContactOrUser>();
    selectedContactsAndUsers.forEach((s) => byId.set(s.id, s));
    fromApi.forEach((p) => byId.set(p.id, p));
    const merged = Array.from(byId.values());
    const sel = new Set(selectedContactsAndUsers.map((x) => x.id));
    merged.sort((a, b) => {
      const ao = sel.has(a.id) ? 0 : 1;
      const bo = sel.has(b.id) ? 0 : 1;
      if (ao !== bo) return ao - bo;
      return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
    });
    return merged;
  }, [
    isCommunityWizardStep2,
    isCommunityFilterStep2,
    communityFilterResultsReady,
    audienceMethod,
    contactsAndUsers,
    selectedContactsAndUsers,
    searchTerm,
  ]);

  const handleCommunityRowToggle = useCallback(
    (item: ContactOrUser, checked: boolean) => {
      if (checked) {
        setSelectedContactsAndUsers((prev) =>
          prev.some((p) => p.id === item.id) ? prev : [...prev, item]
        );
      } else {
        handleRemoveContactOrUser(item.id);
      }
    },
    [handleRemoveContactOrUser]
  );

  const handleToggleSelectAllInvitePicker = useCallback(() => {
    const visible = isCommunityWizardStep2 ? communityInviteTableRows : invitePickerItems;
    if (visible.length === 0) return;
    setSelectedContactsAndUsers((prev) => {
      const visibleIds = new Set(visible.map((i) => i.id));
      const allSelected = visible.every((i) => prev.some((p) => p.id === i.id));
      if (allSelected) {
        return prev.filter((p) => !visibleIds.has(p.id));
      }
      const idSet = new Set(prev.map((p) => p.id));
      const additions = visible.filter((i) => !idSet.has(i.id));
      return [...prev, ...additions];
    });
  }, [isCommunityWizardStep2, communityInviteTableRows, invitePickerItems]);

  const handleAddContact = useCallback(async (contactData: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    accountId?: string;
    title?: string;
    businessUnit?: string;
    image?: string;
  }) => {
    try {
      // Include room's tenantId in the request to ensure contact is created in room's tenant
      const requestBody = {
        ...contactData,
        ...(roomTenantId && { tenantId: roomTenantId }) // Pass room's tenantId if available
      };

      const response = await fetch('/api/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create contact');
      }

      const newContact = await response.json();

      // Convert the new contact to ContactOrUser format and add to selected list
      const contactToAdd: ContactOrUser = {
        id: newContact.id,
        firstName: newContact.firstName,
        lastName: newContact.lastName,
        email: newContact.email,
        title: newContact.title || '',
        type: 'contact',
        accountName: accountName,
        image: newContact.image,
      };

      setSelectedContactsAndUsers(prev => {
        // Check if contact is already in the list
        if (prev.some(c => c.id === contactToAdd.id)) {
          return prev;
        }
        return [...prev, contactToAdd];
      });

      // Clear search term and close suggestions
      setSearchTerm('');
      setShowSuggestions(false);

      toast({
        title: "Contact created",
        description: "The contact has been created and added to your selection.",
      });
      setIsAddContactOpen(false);
      if (showCommunityInviteWizard && audienceMethod === 'filter' && communityWizardStep === 2) {
        void applyCommunityFilters();
      } else {
        void fetchContactsAndUsers();
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      toast({
        title: "Error creating contact",
        description: error instanceof Error ? error.message : "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  }, [
    accountName,
    toast,
    fetchContactsAndUsers,
    roomTenantId,
    showCommunityInviteWizard,
    audienceMethod,
    communityWizardStep,
    applyCommunityFilters,
  ]);

  const handleAddUser = useCallback(async (userData: {
    firstName: string;
    lastName: string;
    email: string;
    password?: string;
    role: string;
    title?: string;
    businessUnit?: string;
    phone?: string;
    userType: 'INTERNAL' | 'EXTERNAL';
    image?: string;
    customMessage?: string;
  }) => {
    setAddUserError(null);
    // Share-room flow always uses AddUserForm without inviteOnly, so password is required for POST /api/users
    if (!userData.password) {
      setAddUserError('Password is required.');
      return;
    }
    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          password: userData.password,
          role: userData.role,
          title: userData.title,
          businessUnit: userData.businessUnit,
          phone: userData.phone,
          userType: userData.userType,
          image: userData.image,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create user');
      }

      toast({
        title: "User created",
        description: "The team member has been created successfully.",
      });
      setIsAddUserOpen(false);
      if (showCommunityInviteWizard && audienceMethod === 'filter' && communityWizardStep === 2) {
        void applyCommunityFilters();
      } else {
        void fetchContactsAndUsers();
      }
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred.';
      setAddUserError(errorMessage);
      toast({
        title: "Error creating user",
        description: errorMessage,
        variant: "destructive",
      });
    }
  }, [toast, fetchContactsAndUsers, showCommunityInviteWizard, audienceMethod, communityWizardStep, applyCommunityFilters]);


  const { handleShare, handleCommunityGuestShare } = useShareRoomInviteSubmit({
    roomId,
    accountId,
    contentId,
    contentName,
    message,
    createShareableLink,
    selectedContactsAndUsers,
    guestInviteRows,
    sessionUserType: session?.user?.userType,
    isCommunityRoomContext,
    communityBulkNoAccount,
    communityBulkAccountId,
    resolveExternalInviteContext,
    setIsSendingInvites,
    setConfirmationMessage,
    setIsShareModalOpen,
    setShowConfirmation,
    setShowConfetti,
    setGuestInviteRows,
    handleCloseConfirmation,
    toast,
    onMembersUpdated,
  });

  const addGuestInviteRow = useCallback(() => {
    const firstName = guestFirstName.trim();
    const lastName = guestLastName.trim();
    if (!firstName || !lastName) {
      toast({
        title: 'First name and last name are required',
        variant: 'destructive',
      });
      return;
    }
    const email = guestEmailField.trim().toLowerCase();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      toast({
        title: 'Enter a valid email address',
        variant: 'destructive',
      });
      return;
    }
    if (guestInviteRows.some((r) => r.email === email)) {
      toast({
        title: 'Already added',
        variant: 'destructive',
      });
      return;
    }
    setGuestInviteRows((prev) => [
      ...prev,
      {
        email,
        firstName,
        lastName,
      },
    ]);
    setGuestEmailField('');
    setGuestFirstName('');
    setGuestLastName('');
  }, [guestEmailField, guestFirstName, guestLastName, guestInviteRows, toast]);

  const applyBulkInvitePaste = useCallback(() => {
    const parsed = parseOutlookStyleEmailInvites(bulkInvitePasteText);
    if (parsed.length === 0) {
      toast({
        title: 'No valid email addresses found',
        description:
          'Example: First last <name@company.com>; Jane Doe <jane@company.com>',
        variant: 'destructive',
      });
      return;
    }
    setGuestInviteRows((prev) => {
      const existing = new Set(prev.map((r) => r.email.toLowerCase()));
      const additions: { email: string; firstName: string; lastName: string }[] = [];
      for (const p of parsed) {
        if (existing.has(p.email)) continue;
        existing.add(p.email);
        additions.push({
          email: p.email,
          firstName: p.firstName,
          lastName: p.lastName,
        });
      }
      queueMicrotask(() => {
        if (additions.length > 0) {
          toast({ title: `Added ${additions.length} recipient(s)` });
        } else {
          toast({
            title: 'No new recipients',
            description: 'Those addresses were already in your list.',
          });
        }
      });
      if (additions.length === 0) return prev;
      return [...prev, ...additions];
    });
    setBulkInvitePasteText('');
  }, [bulkInvitePasteText, toast]);

  useEffect(() => {
    if (
      !isOpen ||
      !isCommunityRoomContext ||
      session?.user?.userType !== 'INTERNAL'
    ) {
      return;
    }
    const q = communityBulkAccountSearch.trim();
    if (q.length < 1) {
      setCommunityBulkAccountHits([]);
      setCommunityBulkAccountSearchLoading(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setCommunityBulkAccountSearchLoading(true);
      void (async () => {
        try {
          const params = new URLSearchParams({ query: q, roomId });
          const res = await fetch(`/api/accounts/search?${params.toString()}`);
          if (!res.ok || cancelled) return;
          const data = (await res.json()) as { id: string; name: string }[];
          const selectedId = communityBulkAccountId;
          if (!cancelled) {
            setCommunityBulkAccountHits(
              data.filter((a) => a.id !== selectedId).slice(0, 8)
            );
          }
        } catch {
          if (!cancelled) setCommunityBulkAccountHits([]);
        } finally {
          if (!cancelled) setCommunityBulkAccountSearchLoading(false);
        }
      })();
    }, 280);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    communityBulkAccountSearch,
    roomId,
    isOpen,
    isCommunityRoomContext,
    session?.user?.userType,
    communityBulkAccountId,
  ]);

  const togglePreview = () => setShowPreview(!showPreview);

  const companyJoinHint = useMemo(() => {
    if (!allowedJoinDomains.length) return null
    return allowedJoinDomains.map((d) => `@${d}`).join(', ')
  }, [allowedJoinDomains])

  const { html: shareEmailPreviewHtml } = useShareRoomEmailPreview({
    roomName,
    message,
    contentId,
    contentName,
    sessionUser: session?.user,
    selectedContactsAndUsers,
  });

  useEffect(() => {
    setIsShareModalOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      inputRef.current &&
      !showCustomerEmailInviteUi &&
      (!showCommunityInviteWizard || communityWizardStep === 2)
    ) {
      inputRef.current.focus();
    }
  }, [isOpen, showCustomerEmailInviteUi, showCommunityInviteWizard, communityWizardStep]);

  if (!isOpen) return null

  return (
    <>
      <Dialog open={isShareModalOpen} onOpenChange={(open) => {
        setIsShareModalOpen(open);
        if (!open) onClose();
      }}>
        <DialogContent
          className={cn(
            'w-full max-w-[95vw]',
            showCommunityInviteWizard &&
              cn(
                'flex max-h-[92vh] min-h-0 flex-col gap-4 overflow-hidden',
                communityWizardStep === 2
                  ? 'min-h-[min(580px,88vh)] sm:max-w-6xl'
                  : 'sm:max-w-5xl'
              ),
            !showCommunityInviteWizard && showPreview && 'sm:max-w-6xl',
            !showCommunityInviteWizard && !showPreview && 'sm:max-w-lg'
          )}
        >
          <DialogHeader className={cn(showCommunityInviteWizard && 'shrink-0 space-y-1.5')}>
            <DialogTitle>
              {contentName
                ? 'Share Content'
                : showCustomerEmailInviteUi
                  ? 'Invite by email'
                  : showCommunityInviteWizard
                    ? 'Invite to community room'
                    : 'Share Room'}
            </DialogTitle>
            <DialogDescription>{roomName}</DialogDescription>
            {contentName && (
              <DialogDescription className="text-xs text-gray-500">Content: {contentName}</DialogDescription>
            )}
          </DialogHeader>

          {!contentName && domainJoinEnabled && (
            <ShareRoomDomainJoinSection
              showCustomerEmailInviteUi={showCustomerEmailInviteUi}
              companyJoinHint={companyJoinHint}
              domainShareLinkLoading={domainShareLinkLoading}
              generatedLink={generatedLink}
              onCopyJoinLink={handleCopyShareLink}
            />
          )}

          {showCommunityInviteWizard && !isSendingInvites && (
            <ShareRoomCommunityStepNav currentStep={communityWizardStep} />
          )}

          <div
            className={cn(
              showCommunityInviteWizard &&
                'min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1 [-webkit-overflow-scrolling:touch]'
            )}
          >
          {showCustomerEmailInviteUi ? (
            <ShareRoomCustomerEmailInviteForm
              roomId={roomId}
              currentUserId={session?.user?.id}
              contentName={contentName}
              accountName={accountName}
              guestFirstName={guestFirstName}
              onGuestFirstNameChange={setGuestFirstName}
              guestLastName={guestLastName}
              onGuestLastNameChange={setGuestLastName}
              guestEmailField={guestEmailField}
              onGuestEmailFieldChange={setGuestEmailField}
              onAddGuestRow={addGuestInviteRow}
              guestInviteRows={guestInviteRows}
              onRemoveGuest={(email) =>
                setGuestInviteRows((prev) => prev.filter((x) => x.email !== email))
              }
              bulkInvitePasteOpen={bulkInvitePasteOpen}
              onBulkInvitePasteOpenChange={setBulkInvitePasteOpen}
              bulkInvitePasteText={bulkInvitePasteText}
              onBulkInvitePasteTextChange={setBulkInvitePasteText}
              onApplyBulkPaste={applyBulkInvitePaste}
              message={message}
              onMessageChange={setMessage}
              isSubmitting={isSendingInvites}
              onSubmit={(existingRoomMembers) => void handleCommunityGuestShare(existingRoomMembers)}
              isPartnerExternalUser={isPartnerExternalUser}
              myCompanyAccount={myCompanyAccount}
              emailInviteAccountContext={emailInviteAccountContext}
              onEmailInviteAccountContextChange={setEmailInviteAccountContext}
            />
          ) : isSendingInvites ? (
            <div className="flex min-h-[12rem] flex-shrink-0 justify-center items-center">
              <p>
                {contentName ? 'Sharing content...' : 'Sending invitations...'}
              </p>
            </div>
          ) : (
            <div
              className={cn(
                'flex flex-col',
                showPreview && (!showCommunityInviteWizard || communityWizardStep === 3) && 'lg:flex-row lg:items-start lg:gap-8'
              )}
            >
              <ShareRoomInternalInviteColumn
                showDirectoryLoadingStrip={
                  isLoading &&
                  session?.user?.userType === 'INTERNAL' &&
                  !showCustomerEmailInviteUi
                }
                showCommunityInviteWizard={showCommunityInviteWizard}
                communityWizardStep={communityWizardStep}
                setCommunityWizardStep={setCommunityWizardStep}
                audienceMethod={audienceMethod}
                setAudienceMethod={setAudienceMethod}
                isCommunityWizardStep2={isCommunityWizardStep2}
                isCommunityFilterStep2={isCommunityFilterStep2}
                communityFilterPanelProps={
                  isCommunityWizardStep2 && audienceMethod === 'filter'
                    ? {
                        roomId,
                        selectedAccountTypes: filterAccountTypes,
                        onToggleAccountType: toggleFilterAccountType,
                        selectedAccounts: filterSelectedAccounts,
                        onAddAccount: addCommunityFilterAccount,
                        onRemoveAccount: removeCommunityFilterAccount,
                        titleContains: filterTitleContains,
                        onTitleContainsChange: setFilterTitleContains,
                        includeContactsWithoutAccount: filterIncludeNoAccount,
                        onIncludeContactsWithoutAccountChange: setFilterIncludeNoAccount,
                        includeInternalUsers: filterIncludeInternalUsers,
                        onIncludeInternalUsersChange: setFilterIncludeInternalUsers,
                        onApply: applyCommunityFilters,
                        isLoading: communityFilterLoading,
                        className: 'min-h-0 lg:max-h-[min(58vh,560px)] lg:overflow-y-auto',
                      }
                    : null
                }
                roomId={roomId}
                accountName={accountName}
                workspaceName={session?.user?.tenantName}
                isCommunityRoom={isCommunityRoom}
                isInternalInRoomTenant={isInternalInRoomTenant}
                selectedContactsAndUsers={selectedContactsAndUsers}
                setSelectedContactsAndUsers={setSelectedContactsAndUsers}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                setShowSuggestions={setShowSuggestions}
                searchTimeout={searchTimeout}
                setSearchTimeout={setSearchTimeout}
                fetchContactsAndUsers={fetchContactsAndUsers}
                communityInviteFilterOptions={communityInviteFilterOptions}
                inputRef={inputRef}
                suggestionsRef={suggestionsRef}
                showSuggestions={showSuggestions}
                contactsAndUsers={contactsAndUsers}
                communityFilterLoading={communityFilterLoading}
                communityFilterResultsReady={communityFilterResultsReady}
                communityInviteTableRows={communityInviteTableRows}
                selectedIdSet={selectedIdSet}
                handleCommunityRowToggle={handleCommunityRowToggle}
                handleToggleSelectAllInvitePicker={handleToggleSelectAllInvitePicker}
                invitePickerItems={invitePickerItems}
                handleSelectContactOrUser={handleSelectContactOrUser}
                handleRemoveContactOrUser={handleRemoveContactOrUser}
                contentName={contentName}
                onSelectAllRoomMembers={() => void handleSelectAllRoomMembers()}
                isSelectingAllRoomMembers={isSelectingAllRoomMembers}
                onAddAllContacts={handleAddAllContacts}
                addAllContactsCount={addAllContacts.length}
                directoryTruncated={directoryTruncated}
                directoryLimit={directoryLimit}
                guestInviteRows={guestInviteRows}
                setGuestInviteRows={setGuestInviteRows}
                setAddContactInitialEmail={setAddContactInitialEmail}
                setIsAddContactOpen={setIsAddContactOpen}
                setIsAddUserOpen={setIsAddUserOpen}
                showCommunityBulkCrmSection={
                  session?.user?.userType === 'INTERNAL' &&
                  isCommunityRoomContext &&
                  guestInviteRows.length > 0
                }
                communityBulkNoAccount={communityBulkNoAccount}
                setCommunityBulkNoAccount={setCommunityBulkNoAccount}
                communityBulkAccountId={communityBulkAccountId}
                setCommunityBulkAccountId={setCommunityBulkAccountId}
                communityBulkAccountName={communityBulkAccountName}
                setCommunityBulkAccountName={setCommunityBulkAccountName}
                communityBulkAccountSearch={communityBulkAccountSearch}
                setCommunityBulkAccountSearch={setCommunityBulkAccountSearch}
                communityBulkAccountHits={communityBulkAccountHits}
                setCommunityBulkAccountHits={setCommunityBulkAccountHits}
                communityBulkAccountSearchLoading={communityBulkAccountSearchLoading}
                bulkInvitePasteOpen={bulkInvitePasteOpen}
                setBulkInvitePasteOpen={setBulkInvitePasteOpen}
                bulkInvitePasteText={bulkInvitePasteText}
                setBulkInvitePasteText={setBulkInvitePasteText}
                applyBulkInvitePaste={applyBulkInvitePaste}
                showCommunityWizardStep2Nav={
                  showCommunityInviteWizard && communityWizardStep === 2
                }
                showMessageFooter={
                  !showCommunityInviteWizard || communityWizardStep === 3
                }
                messageFooterProps={{
                  contentName,
                  message,
                  onMessageChange: setMessage,
                  isInternalInRoomTenant,
                  domainJoinEnabled,
                  createShareableLink,
                  onCreateShareableLinkCheckedChange: handleCreateShareableLinkToggle,
                  generatedLink,
                  onCopyGeneratedShareableLink: handleCopyShareLink,
                  showPreview,
                  onTogglePreview: togglePreview,
                  communityWizardReviewLayout: Boolean(
                    showCommunityInviteWizard && communityWizardStep === 3
                  ),
                  onCommunityWizardBack: () => setCommunityWizardStep(2),
                  onSubmit: handleShare,
                  isSendingInvites,
                  canSubmit:
                    selectedContactsAndUsers.length > 0 || guestInviteRows.length > 0,
                }}
              />

              {/* Right Column: Preview - internal users only */}
              {isInternalInRoomTenant &&
                showPreview &&
                (!showCommunityInviteWizard || communityWizardStep === 3) && (
                  <ShareRoomEmailPreviewPanel
                    html={shareEmailPreviewHtml}
                    heading={contentName ? 'Email preview' : 'Invitation preview'}
                  />
                )}
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>

      <ShareRoomSuccessOverlay
        confettiActive={showConfetti}
        confirmationOpen={showConfirmation}
        confirmationMessage={confirmationMessage}
        onConfirmationClose={handleCloseConfirmation}
        onConfettiFade={handleConfettiFade}
      />

      {/* Add Contact Modal */}
      <AddContactForm
        isOpen={isAddContactOpen}
        onClose={() => {
          setIsAddContactOpen(false);
          setAddContactInitialEmail('');
        }}
        onSubmit={(contactData) => {
          handleAddContact(contactData);
        }}
        accountId={isCommunityRoom ? undefined : accountId}
        hideOptionalFields={true}
        inviteContext="room"
        initialEmail={addContactInitialEmail}
      />

      {/* Add User Modal */}
      <AddUserForm
        isOpen={isAddUserOpen}
        onClose={() => {
          setIsAddUserOpen(false);
          setAddUserError(null);
        }}
        onSubmit={handleAddUser}
        error={addUserError}
      />
    </>
  )
}
